import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { businessStayTexts } from '../config/businessStayTexts';
import { useLanguage } from '../context/LanguageContext';

const BusinessStayPreviewSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
  background-color: ${({ theme }) => theme.colors.light};
`;

const BusinessStayPreviewContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  position: relative;
  padding-bottom: ${({ theme }) => theme.spacing.md};
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.primary};
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 1.1rem;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

const PackagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const PackageCard = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  overflow: hidden;
  transition: transform ${({ theme }) => theme.transitions.medium}, box-shadow ${({ theme }) => theme.transitions.medium};
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${({ theme }) => theme.shadows.large};
  }
`;

const RoomImageTop = styled.div`
  height: 200px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
`;

const PackageTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
`;

const PackageRoom = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-weight: 500;
`;

const PackageContent = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`;

const PackageDescription = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.6;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PackagePrice = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.md} 0;
  border-top: 2px solid ${({ theme }) => theme.colors.light};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Price = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const PriceLabel = styled.p`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const TaxNote = styled.p`
  text-align: center;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textLight};
  font-style: italic;
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const ViewAllButton = styled(Link)`
  display: block;
  width: fit-content;
  margin: ${({ theme }) => theme.spacing.xl} auto 0;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-weight: 600;
  text-decoration: none;
  transition: all ${({ theme }) => theme.transitions.medium};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
    border-color: ${({ theme }) => theme.colors.secondary};
  }
`;

const BusinessStayPreview = () => {
  const { language } = useLanguage();
  const texts = businessStayTexts[language] || businessStayTexts.fr;
  
  const businessStayUrl = language === 'fr' ? 'soirees-etapes' : 'business-stays';
  
  return (
    <BusinessStayPreviewSection>
      <BusinessStayPreviewContainer>
        <SectionTitle>{texts.pageTitle}</SectionTitle>
        <SectionSubtitle>{texts.intro.description}</SectionSubtitle>
        
        <PackagesGrid>
          {texts.packages.map((pkg) => {
            const roomImage = pkg.id === 'traditional' ? '/images/simple.jpg' : '/images/design.jpg';
            return (
              <PackageCard key={pkg.id}>
                <RoomImageTop image={roomImage} />
                <PackageContent>
                  <PackageTitle>{pkg.title}</PackageTitle>
                  <PackageRoom>{pkg.room}</PackageRoom>
                  <PackageDescription>{pkg.description}</PackageDescription>
                  
                  <PackagePrice>
                    <Price>{pkg.price}€*</Price>
                    <PriceLabel>{language === 'fr' ? 'par personne' : 'per person'}</PriceLabel>
                  </PackagePrice>
                </PackageContent>
              </PackageCard>
            );
          })}
        </PackagesGrid>
        
        <TaxNote>
          {language === 'fr' ? '*Taxe de séjour en sus (1,90€ par personne)' : '*Tourist tax extra (€1.90 per person)'}
        </TaxNote>
        
        <ViewAllButton to={`/${language}/${businessStayUrl}`}>
          {language === 'fr' ? 'Réserver une soirée étape' : 'Book a business stay'}
        </ViewAllButton>
      </BusinessStayPreviewContainer>
    </BusinessStayPreviewSection>
  );
};

export default BusinessStayPreview;
